<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import paypalIcon from '@h5/assets/paypal.png'
import stripeIcon from '@h5/assets/stripe.png'
import { usePurchase } from '@/composables/payment/composables/usePurchase'
import { useSubscription } from '@/composables/payment/composables/useSubscription'
import { PaymentMethodEnum } from '@/composables/payment/composables/constants'
import PaymentRechargeSection from '@/composables/payment/components/PaymentRechargeSection.vue'

defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const {
  templateProducts,
  availablePaymentMethods: purchaseAvailableMethods,
  currentPaymentMethod: purchasePaymentMethod,
  selectPaymentMethod: selectPurchasePaymentMethod,
  PayTemplate,
  loading: purchaseLoading,
  payLoading,
} = usePurchase()

const {
  selectPaymentMethod: selectSubscribePaymentMethod,
  loaded: subscribeLoaded,
} = useSubscription()

const activeIndex = ref(0)

const availablePaymentMethods = computed(() => {
  return purchaseAvailableMethods.value || []
})

const currentPaymentMethod = computed({
  get: () => purchasePaymentMethod.value,
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscribePaymentMethod(val)
  },
})

// 确保 currentPaymentMethod 始终在 availablePaymentMethods 中
watch(availablePaymentMethods, (methods) => {
  if (methods.length && !methods.find(m => Number(m.paymentMethod) === Number(currentPaymentMethod.value)))
    currentPaymentMethod.value = Number(methods[0].paymentMethod)
}, { immediate: true })

const showMethodSelector = computed(() => availablePaymentMethods.value.length > 1)
const isLoading = computed(() => purchaseLoading.value || !subscribeLoaded.value)

const PAYMENT_ICONS: Record<number, string> = {
  [PaymentMethodEnum.PAYPAL]: paypalIcon,
  [PaymentMethodEnum.STRIPE]: stripeIcon,
}

function selectMethod(method: number) {
  currentPaymentMethod.value = method
  activeIndex.value = 0
}

function handleRechargeSelect(item: any, index: number) {
  activeIndex.value = index
  PayTemplate(item)
}
</script>

<template>
  <Teleport to="body">
    <div v-if="visible" class="pay-popup-mask" @click.self="emit('close')">
      <div class="pay-popup">
        <button class="pay-popup-close" @click="emit('close')">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="#65686F" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>

        <div class="pay-popup-header">
          <h3 class="pay-popup-title">
            Top Up
          </h3>
        </div>

        <div class="pay-popup-body">
          <div v-if="showMethodSelector" class="pay-method-selector">
            <button
              v-for="method in availablePaymentMethods"
              :key="method.paymentMethod"
              class="pay-method-btn"
              :class="{ active: Number(currentPaymentMethod) === Number(method.paymentMethod) }"
              @click="selectMethod(Number(method.paymentMethod))"
            >
              <div class="pay-method-icon-wrap">
                <img :src="PAYMENT_ICONS[method.paymentMethod]" class="pay-method-icon">
              </div>
            </button>
          </div>

          <div v-if="isLoading" class="pay-loading">
            <BasicSpinner />
          </div>

          <template v-else>
            <PaymentRechargeSection
              v-if="templateProducts.length"
              :products="templateProducts"
              :active-index="activeIndex"
              @select="handleRechargeSelect"
            />
          </template>

          <div class="pay-popup-tips">
            <p class="tips-title">
              Tips:
            </p>
            <p>1. Coins are virtual items and cannot be refunded. They can only be used within this app.</p>
            <p>2. Reward coins are time-sensitive. Please use them within the 7-day validity period, as they will be deducted first when unlocking content.</p>
            <p>3. For other questions, please contact us via Profile &gt; Customer Service.</p>
          </div>
        </div>

        <div v-if="payLoading" class="pay-submit-loading">
          <BasicSpinner />
          <span class="pay-submit-loading-text">Processing...</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.pay-popup-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.pay-popup {
  position: relative;
  width: 455px;
  max-height: 642px;
  background-color: #FFFFFF;
  background-image: url('/images/pc/recharge_bg.webp');
  background-repeat: no-repeat;
  background-size: 100% 185px;
  background-position: top;
  border-radius: 8px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  box-shadow: 0px 12px 32px 0px rgba(0, 0, 0, 0.16);
}
.pay-popup::-webkit-scrollbar {
  display: none;
}

.pay-popup-close {
  position: absolute;
  top: 8px;
  right: 20px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;
}
.pay-popup-close:hover {
  opacity: 1;
}

.pay-popup-header {
  text-align: center;
  padding: 20px 20px 16px;
}

.pay-popup-title {
  font-weight: bold;
  font-size: 22px;
  color: #323435;
  margin: 0;
}

.pay-popup-body {
  padding: 0 20px 20px;
}

.pay-method-selector {
  width: 284px;
  height: 40px;
  background: #E2EAF7;
  border-radius: 20px;
  margin: 0 auto 16px;
  display: flex;
  padding: 4px;
  box-sizing: border-box;
}

.pay-method-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 20px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}
.pay-method-btn.active {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
}

.pay-method-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pay-method-icon {
  height: 20px;
  min-width: 50px;
  object-fit: contain;
  display: block;
}

.pay-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.pay-submit-loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
}

.pay-submit-loading-text {
  margin-top: 12px;
  font-size: 14px;
  color: #65686F;
}

.pay-popup-tips {
  padding-top: 12px;
  font-size: 10px;
  color: #9296A0;
  line-height: 1.6;
}
.pay-popup-tips .tips-title {
  font-size: 12px;
  margin-bottom: 6px;
}
.pay-popup-tips p {
  margin: 6px 0 0;
}
.pay-popup-tips .tips-title {
  margin-top: 0;
}
</style>
